import { CORE_MODULES, MODULE_CODES, PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useCreateTenant, useUpdateTenant } from '../api/mutations.js';
import { useAttachmentCredentials, useTenant } from '../api/queries.js';
import {
  type AttachmentCredential,
  type TenantDetail,
  type TenantFormInput,
  tenantCreateInput,
  tenantFormInput,
} from '../api/schemas.js';
import { TenantAttachmentCredentials } from '../forms/tenant-attachment-credentials.js';
import { TenantFields } from '../forms/tenant-fields.js';

/**
 * Creating and editing a tenant.
 *
 * One component for both, because they are the same fourteen fields and the
 * same validation; only the defaults, the title and the mutation differ.
 * Splitting them would mean two files that have to be kept in step by hand.
 * Which one this is comes from the route, not a prop, so the URL is the truth.
 */

/** 409. Named because the linter forbids a bare numeric literal here. */
const HTTP_CONFLICT = 409;

/** 404. Named for the same reason. */
const HTTP_NOT_FOUND = 404;

const CREATE_DEFAULTS: Partial<TenantFormInput> = {
  slug: '',
  name: '',
  status: 'PENDING',
  timezone: 'UTC',
  // Every module ticked. Selling is a subtraction: the operator unticks what
  // this customer did not buy, rather than remembering to tick what they did.
  // An empty default is not neutral — the owner's permissions are role grants
  // intersected with the tenant's modules, so a tenant created with none has
  // an administrator who sees only Users and the audit log, cannot create a
  // property, and cannot even reach the dashboard.
  //
  // It has to be stated here rather than left to the server: the form always
  // sends this field, and an explicit [] is not the same as omitting it, so
  // the server's own all-modules default never fires for a form submission.
  enabledModules: MODULE_CODES.filter((code) => !CORE_MODULES.includes(code)),
  contactName: '',
  phoneExtension: '',
  phone: '',
  address: '',
  countryId: '',
  stateId: '',
  cityId: '',
  email: '',
  attachmentCredentials: {
    AWS_REGION: '',
    AWS_S3_BUCKET: '',
    AWS_ACCESS_KEY_ID: '',
    AWS_SECRET_ACCESS_KEY: '',
  },
};

/** Null columns become empty strings: a controlled input cannot take null. */
function defaultsFrom(
  tenant: TenantDetail,
  attachmentCredentials: AttachmentCredential | null,
): Partial<TenantFormInput> {
  return {
    slug: tenant.slug,
    name: tenant.name,
    status: tenant.status as TenantFormInput['status'],
    timezone: tenant.timezone,
    enabledModules: [...tenant.enabledModules],
    contactName: tenant.contactName ?? '',
    phoneExtension: tenant.phoneExtension ?? '',
    phone: tenant.phone ?? '',
    address: tenant.address ?? '',
    countryId: tenant.countryId ?? '',
    stateId: tenant.stateId ?? '',
    cityId: tenant.cityId ?? '',
    email: tenant.email ?? '',
    ...(attachmentCredentials === null
      ? {}
      : { attachmentCredentials: attachmentCredentials.credentials }),
  };
}

export function TenantFormPage(): React.JSX.Element {
  const tenantId = useParams().tenantId;
  const isEdit = tenantId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.TENANT_UPDATE : PERMISSIONS.TENANT_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditTenant tenantId={tenantId} /> : <CreateTenant />}
    </PermissionGuard>
  );
}

function CreateTenant(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateTenant();

  const form = useAppForm<TenantFormInput>({
    // The create-only schema: an owner is required here and cannot be set on
    // edit, so the requirement lives on this form rather than the shared shape.
    schema: tenantCreateInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Tenant created',
    onSubmit: async (values) => {
      await create.mutateAsync(tenantCreateInput.parse(values));
      void navigate('/tenants');
    },
  });

  return (
    <FormPage
      title="New tenant"
      description="A tenant is the top-level account that properties, users and bookings belong to."
      backTo="/tenants"
      backLabel="Tenants"
      submitLabel="Create tenant"
      submitting={form.submitting}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TenantFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
      <TenantAttachmentCredentials
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditTenant({ tenantId }: { readonly tenantId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateTenant();
  const query = useTenant(tenantId);
  const credentialsQuery = useAttachmentCredentials(tenantId);

  // The form is mounted only once the record is loaded, so `defaultValues` is
  // populated on first render. Passing them in later needs an effect and a
  // reset, and any field the user has already touched is then discarded.
  if (query.isPending) return <LoadingState label="Loading tenant" />;
  if (query.error !== null) {
    // An ErrorState rather than NotPermitted: a failed load is not a
    // permission problem, and saying it is sends the reader to the wrong fix.
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (credentialsQuery.isPending)
    return <LoadingState label="Loading tenant storage credentials" />;

  if (
    credentialsQuery.error !== null &&
    !(
      credentialsQuery.error instanceof ApiError && credentialsQuery.error.status === HTTP_NOT_FOUND
    )
  ) {
    return (
      <ErrorState
        description={errorTextFor(credentialsQuery.error) ?? ''}
        onRetry={() => {
          void credentialsQuery.refetch();
        }}
      />
    );
  }

  return (
    <EditTenantForm
      tenant={query.data}
      attachmentCredentials={credentialsQuery.data ?? null}
      onSaved={() => {
        void navigate('/tenants');
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ tenantId, version: query.data.version, values });
        } catch (error) {
          // Refetch on conflict only. The message is already on its way to the
          // user from useAppForm; what it cannot do is give the form a version
          // that is not stale, so a second attempt would fail identically.
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditTenantForm({
  tenant,
  attachmentCredentials,
  save,
  onSaved,
}: {
  readonly tenant: TenantDetail;
  readonly attachmentCredentials: AttachmentCredential | null;
  readonly save: (values: TenantFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<TenantFormInput>({
    schema: tenantFormInput,
    defaultValues: defaultsFrom(tenant, attachmentCredentials),
    successMessage: 'Tenant updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${tenant.name}`}
      description="Changes take effect immediately for everyone using this tenant."
      // The list, not the record being edited. Cancel means "I did not want to
      // make this change", and landing back on the detail screen of the record
      // you just abandoned reads as though something was saved. Every other
      // form screen in the portal goes back to its list; this was the outlier.
      backTo="/tenants"
      backLabel="Tenants"
      submitLabel="Save changes"
      submitting={form.submitting}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <TenantFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
      <TenantAttachmentCredentials
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
